import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import DialogContent from '@mui/material/DialogContent'
import Dialog from '@mui/material/Dialog'
import { path, pathOr, prop, propOr } from 'ramda'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox/Checkbox'
import DialogActions from '@mui/material/DialogActions/DialogActions'
import InfiniteScroll from 'react-infinite-scroll-component'
import { styled } from '@mui/material'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useField } from 'react-final-form'
import TableHead from '@mui/material/TableHead'
import { useTranslation } from 'react-i18next'

import DialogTitle from '~/components/DialogTitle'
import TextOverflow from '~/components/TextOverflow'
import Avatar from '~/components/Avatar'
import { Button, DiscardButton } from '~/components/Buttons'
import CurrencyMoney from '~/components/Money/CurrencyMoney'
import Status, { ERROR, SUCCESS } from '~/components/Status'
import InfinityScrollSearch from '~/components/InfinityScrollSearch'

import VariantTableSkeletonList from './VariantTableSkeletonList'

const TableRowStyled = styled(TableRow)({
  cursor: 'pointer',
  '&:hover': {
    background: '#0000000A'
  }
})

const DialogContentStyled = styled(DialogContent)({
  padding: '0',
  position: 'relative',
  height: 'calc(100vh / 2)'
})

function ReturnVariantAddDialog ({ open, onClose, onAddVariants, params, returnVariants }) {
  const { t } = useTranslation()

  const lineItemsField = useField('lineItems')
  const lineItemsFieldValue = lineItemsField.input.value || []

  const [selected, setSelected] = useState(lineItemsFieldValue)

  const results = propOr([], 'results', returnVariants)
  const count = prop('count', returnVariants)

  const handleCheck = (checked, variant) => {
    const guid = prop('guid', variant)
    const newVariant = { ...variant, unit: { guid } }
    if (checked) {
      setSelected(prevSelected => {
        return prevSelected.filter(select => path(['unit', 'guid'], select) !== prop('guid', variant))
      })
    } else {
      setSelected(prevSelected => [...prevSelected, newVariant])
    }
  }

  const searchField = useField('search')
  const searchFieldValue = searchField.input.value

  const onEscape = useCallback(event => {
    if (event.code === 'Escape') {
      onClose()
    }
  }, [onClose])

  return (
    <Dialog open={open} maxWidth="md" fullWidth={true} onKeyDown={onEscape}>
      <DialogTitle>
        <Typography
          component="h2"
          variant="h6"
        >
          {`${t('Browse Variants')} - ${t('Selected')} (${selected.length})`}
        </Typography>
        <Box sx={{ padding: '24px 0' }}>
          <InfinityScrollSearch
            onEffect={returnVariants.reset}
            getList={returnVariants.getList}
            params={params}
          />
        </Box>
      </DialogTitle>
      <DialogContentStyled id="returnVariantTable">
        <InfiniteScroll
          dataLength={results.length}
          hasMore={count > results.length}
          loader=""
          next={() => returnVariants.getList({ search: searchFieldValue, ...params })}
          scrollableTarget="returnVariantTable"
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={70}>{t('Select')}</TableCell>
                  <TableCell width={70}>{t('Image')}</TableCell>
                  <TableCell width={240}>{t('Variant')}</TableCell>
                  <TableCell width={220}>{t('Price')}</TableCell>
                  <TableCell width={190}>{t('Location')}</TableCell>
                  <TableCell width={90}>{t('Available')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((product, index) => {
                  const checked = selected.find(select => prop('guid', product) === path(['unit', 'guid'], select))
                  const totalAvailable = propOr(0, 'quantity', product)
                  const statusColor = totalAvailable > 0 ? SUCCESS : ERROR
                  const variant = prop('variant', product)
                  const location = pathOr('', ['location', 'locationId'], product)
                  const src = prop('defaultImage', variant)
                  return (
                    <TableRowStyled onClick={() => handleCheck(checked, product)}>
                      <TableCell data-cy={`variant-${index}`}>
                        <Checkbox checked={Boolean(checked)} color="secondary" />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          alt={prop('name', variant)}
                          src={src}
                          variant="rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <TextOverflow
                          lines={1}
                          selfTooltip={true}
                        >
                          {prop('name', variant)}
                        </TextOverflow>
                      </TableCell>
                      <TableCell>
                        <CurrencyMoney value={prop('price', product)} />
                      </TableCell>
                      <TableCell>
                        {location}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          <TextOverflow tooltip="Available quantity" >
                            <Status
                              label={totalAvailable}
                              color={statusColor}
                            />
                          </TextOverflow>
                        </Typography>
                      </TableCell>
                    </TableRowStyled>
                  )
                })}
                {returnVariants.isLoading && (
                  <VariantTableSkeletonList />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      </DialogContentStyled>
      <DialogActions>
        <DiscardButton
          onClick={onClose}
          fullWidth={true}
        >
          Discard
        </DiscardButton>
        <Button
          variant="contained"
          data-cy="add"
          fullWidth={true}
          onClick={() => onAddVariants(selected)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ReturnVariantAddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddVariants: PropTypes.func.isRequired,
  params: PropTypes.object,
  returnVariants: PropTypes.object.isRequired
}

export default ReturnVariantAddDialog
